import { useState, useEffect } from 'react';
import { formatDateInput, parseDateText, formatDateToString } from './dateUtils';

/**
 * Composant d'input de date avec formatage automatique JJ/MM/AAAA
 * @param {object} props
 * @param {string} props.label - Le libellé du champ
 * @param {Date|null} props.value - La valeur Date
 * @param {function} props.onChange - Callback appelé avec la nouvelle Date
 * @param {string} props.placeholder - Placeholder (défaut: "JJ/MM/AAAA")
 * @param {string} props.className - Classes CSS additionnelles
 * @param {React.ReactNode} props.suggestion - Élément de suggestion optionnel
 * @param {string} props.clearLabel - When set, shows a button that empties the field, with this label
 */
const DateInput = ({ 
  label, 
  value, 
  onChange, 
  placeholder = "JJ/MM/AAAA",
  className = "",
  suggestion = null,
  clearLabel = '',
}) => {
  const [textValue, setTextValue] = useState('');

  // Synchroniser la valeur texte avec la valeur Date
  useEffect(() => {
    if (value instanceof Date && !isNaN(value)) {
      setTextValue(formatDateToString(value));
    } else if (!value) {
      setTextValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setTextValue(formatted);
    
    // Tenter de parser la date si le format est complet
    const parsedDate = parseDateText(formatted);
    onChange(parsedDate);
  };

  const handleBlur = (e) => {
    const parsedDate = parseDateText(e.target.value);
    if (parsedDate) {
      onChange(parsedDate);
      setTextValue(formatDateToString(parsedDate));
    } else if (e.target.value.length === 10) {
      // Format complet mais date invalide, réinitialiser
      setTextValue('');
      onChange(null);
    }
  };

  const handleClear = (e) => {
    // The button sits inside the label: keep the click from focusing the input.
    e.preventDefault();
    setTextValue('');
    onChange(null);
  };

  const showClear = !!clearLabel && textValue !== '';

  return (
    <div className={'w-full'}>
      <label className="ft-sm text-gray-500">
        <div>{label}</div>
        <div className={clearLabel ? 'relative' : undefined}>
          <input
            type="text"
            placeholder={placeholder}
            maxLength={10}
            className={`w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center text-gray-900 ${className}`}
            value={textValue}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title={clearLabel}
              aria-label={clearLabel}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {suggestion}
      </label>
    </div>
  );
};

export default DateInput;
